/**
 * Folha de estilo do PROGRAMA — fonte ÚNICA, usada na tela e embutida no HTML
 * exportado. É o que o usuário edita para customizar o arquivo baixado, então
 * vale a regra: poucas classes, nomes que se explicam, nada de estilo inline
 * no markup.
 *
 * Tudo é escopado por `.program`, o que permite injetá-la na página do admin
 * sem vazar para o resto do sistema.
 *
 * Os COMENTÁRIOS da folha vêm de fora, já traduzidos (`ProgramSheetComments`):
 * quem baixa o arquivo pode não falar português, e comentário que o leitor não
 * entende não ajuda ninguém a editar. As chaves vivem em
 * `admin.event.sessions.program.sheet.*`.
 */

/** Comentários da folha, no idioma da interface. */
export interface ProgramSheetComments {
  intro: string;
  colors: string;
  typography: string;
  reset: string;
  session: string;
  sessionText: string;
  meta: string;
  papers: string;
  links: string;
  paper: string;
  lightOnly: string;
  sessionColors: string;
}

/** Título do bloco de cores gerado ao fim da folha (ver `sessionColorRules`). */
export function programSheetColorsHeader(comments: ProgramSheetComments): string {
  return `/* ${comments.sessionColors} */`;
}

export function programSheetCss(comments: ProgramSheetComments): string {
  return `
/* ${comments.intro} */

/* ${comments.paper} */
:root .light-theme {
  --program-paper: white; //admin-background
}
:root .dark-theme {
  --program-paper: black; //admin-background-dark
}

body {
  background-color: var(--program-paper);
}

.light-theme .program {
  /* ${comments.colors} */
  --session-color: #BFE7FF;
  --session-text: #373734; //sidebar-admin-menuitem-color
  --text: #373734 //sidebar-admin-menuitem-color
  --muted: #6b6b66;
  --rule: #e0dedb;
  --link-hover: #064ad9; //accent-background

  /* ${comments.typography} */
  font-family: Arial, "Helvetica Neue", Helvetica, sans-serif;
  font-size: 14px;
  line-height: 1.5;

  max-width: 820px;
  margin: 0 auto;
  padding: 32px 24px 64px;
  background-color: var(--program-paper);
  color: var(--text);
  /* ${comments.lightOnly} */
  color-scheme: light;
}

.dark-theme .program {
  /* ${comments.colors} */
  --session-color: #BFE7FF;
  --session-text: #373734; //sidebar-admin-menuitem-color
  --text: #c4c2bd; //sidebar-admin-menuitem-color-dark
  --muted: #6b6b66;
  --rule: #e0dedb;
  --link-hover: #60a5fa; //accent-background-dark

  /* ${comments.typography} */
  font-family: Arial, "Helvetica Neue", Helvetica, sans-serif;
  font-size: 14px;
  line-height: 1.5;

  max-width: 820px;
  margin: 0 auto;
  padding: 32px 24px 64px;
  background-color: var(--program-paper);
  color: var(--text);
  /* ${comments.lightOnly} */
  color-scheme: light;
}

/* ${comments.reset} */
.program h1,
.program h2,
.program p,
.program ol,
.program li {
  margin: 0;
  padding: 0;
  font: inherit;
  color: inherit;
  list-style: none;
}

.program .program-title {
  margin: 0 0 24px;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.25;
}

/* ${comments.session} */
.program .session {
  margin: 0 0 28px;
}

.program .session-header {
  padding: 10px 14px;
  border-radius: 6px;
  background-color: var(--session-color);
  /* ${comments.sessionText} */
  color: var(--session-text);
}

.program .session-title {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
}

.program .session-subtitle {
  font-weight: 400;
}

/* ${comments.meta} */
.program .session-meta {
  margin: 4px 0 0;
  font-size: 13px;
  line-height: 1.4;
  opacity: 0.75;
}

/* ${comments.papers} */
.program .papers {
  margin: 0;
  padding: 0;
}

.program .paper {
  padding: 12px 14px;
  border-bottom: 1px solid var(--rule);
}

.program .paper:last-child {
  border-bottom: 0;
}

.program .paper-title {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
}

.program .paper-authors {
  margin: 2px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: var(--muted);
}

.program .paper-abstract {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--muted);
}

/* ${comments.links} */
.program a {
  color: inherit;
  text-decoration: none;
}

.program a:hover {
  color: var(--link-hover);
  text-decoration: none;
}
`;
}
